import React, { createContext, useContext, useState, useEffect } from 'react';
import { authApi } from '../services/authApi';
import type { Distributor, SubscriptionSummary } from '../services/authApi';
import { logoService } from '../services/logoService';
import { queryCache } from '../services/queryCache';

interface User {
  id: number;
  email: string;
  name?: string;
  role?: string;
  plan: string;
  permissions: string[];
  distributor: Distributor | null;
  agent_name?: string;
  agent_slug?: string | null;
  subscription?: SubscriptionSummary | null;
  can_escalate?: boolean;
  phone?: string | null;
  auth_provider?: string | null;
}

interface AuthContextType {
  user: User | null;
  token: string | null;
  login: (email: string, password: string) => Promise<void>;
  loginWithSSO: (
    idToken: string,
    provider: 'google' | 'apple',
    options?: { name?: string }
  ) => Promise<void>;
  logout: () => void;
  updateUser: (partial: Partial<User>) => void;
  isAuthenticated: boolean;
  isInitialized: boolean;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export const AuthProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  // Initialize states
  const [user, setUser] = useState<User | null>(null);
  const [token, setToken] = useState<string | null>(null);
  const [isInitialized, setIsInitialized] = useState(false);

  // Load auth state from localStorage on mount
  useEffect(() => {
    const initializeAuth = () => {
      try {
        const storedToken = localStorage.getItem('token');
        const storedRefreshToken = localStorage.getItem('refresh_token');
        const storedUserStr = localStorage.getItem('user');

        if (storedToken && storedRefreshToken && storedUserStr) {
          const parsedUser = JSON.parse(storedUserStr);
          setToken(storedToken);
          setUser(parsedUser);

          // Preload the distributor logo for better UX
          if (parsedUser.distributor?.logo_filename) {
            logoService.preloadLogo(parsedUser.distributor.logo_filename).catch(error => {
              console.warn('Failed to preload logo:', error);
            });
          }
        }
      } catch (error) {
        console.error('Failed to initialize auth from localStorage', error);
        // Clear potentially corrupted storage
        localStorage.removeItem('token');
        localStorage.removeItem('refresh_token');
        localStorage.removeItem('user');
      } finally {
        setIsInitialized(true);
      }
    };

    initializeAuth();
  }, []);

  const login = async (email: string, password: string) => {
    const response = await authApi.login(email, password);
    const { token: newToken, refresh_token: refreshToken, user: newUser } = response.data;

    queryCache.clear();
    localStorage.setItem('token', newToken);
    localStorage.setItem('refresh_token', refreshToken);
    localStorage.setItem('user', JSON.stringify(newUser));

    setToken(newToken);
    setUser(newUser);

    // Preload the distributor logo for better UX
    if (newUser.distributor?.logo_filename) {
      logoService.preloadLogo(newUser.distributor.logo_filename).catch(error => {
        console.warn('Failed to preload logo:', error);
      });
    }
  };

  const loginWithSSO = async (
    idToken: string,
    provider: 'google' | 'apple',
    options?: { name?: string }
  ) => {
    const response = await authApi.sso(idToken, provider, options);
    const { token: newToken, refresh_token: refreshToken, user: newUser } = response.data;

    queryCache.clear();
    localStorage.setItem('token', newToken);
    localStorage.setItem('refresh_token', refreshToken);
    localStorage.setItem('user', JSON.stringify(newUser));

    setToken(newToken);
    setUser(newUser);

    if (newUser.distributor?.logo_filename) {
      logoService.preloadLogo(newUser.distributor.logo_filename).catch(error => {
        console.warn('Failed to preload logo:', error);
      });
    }
  };

  const logout = () => {
    authApi.logout();
    setToken(null);
    setUser(null);
  };

  const updateUser = (partial: Partial<User>) => {
    if (!user) return;
    const next = { ...user, ...partial };
    setUser(next);
    try {
      localStorage.setItem('user', JSON.stringify(next));
    } catch (e) {
      console.warn('Failed to persist user update', e);
    }
  };

  const refreshToken = async () => {
    try {
      const response = await authApi.refresh();
      const { token: newToken, refresh_token: newRefreshToken } = response.data;

      localStorage.setItem('token', newToken);
      localStorage.setItem('refresh_token', newRefreshToken);

      setToken(newToken);
      return true;
    } catch (error) {
      console.error('Failed to refresh token:', error);
      // If refresh fails, logout the user
      logout();
      return false;
    }
  };

  const value = {
    user,
    token,
    login,
    loginWithSSO,
    logout,
    updateUser,
    refreshToken,
    isAuthenticated: !!token,
    isInitialized,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};

export function useAuth() {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
}
